import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { gamesApi } from '../../api/games';
import getGameTypeUtil from '../../utils/games';
import { getGameImage } from '../../utils/getGameImage';

export default function ActiveGamesPackage({ isOpen, onClose, userData }) {
  const { tokens } = useAuth();
  const [games, setGames] = useState([]);
  const [activeTab, setActiveTab] = useState('All');
  const [loading, setLoading] = useState(true);
  const [tabs, setTabs] = useState(['All']);

  useEffect(() => {
    if (isOpen && userData?.id) {
      fetchUserGames();
    }
  }, [isOpen, userData]);

  const fetchUserGames = async () => {
    try {
      setLoading(true);
      const response = await gamesApi.getUserGame(
        tokens.access.token,
        userData.id
      );
      if (response.status === 200) {
        // Sort games by type for better organization
        const sortedGames = response.data.sort((a, b) => {
          // First sort by game type
          const typeA = getGameType(a);
          const typeB = getGameType(b);
          return typeA.localeCompare(typeB);
        });

        // Generate tabs dynamically based on available game types
        const gameTypes = sortedGames.map((game) => getGameType(game));
        const uniqueTypes = [...new Set(gameTypes)];

        // Add "All" as the first tab and sort the rest alphabetically
        setTabs(['All', ...uniqueTypes.sort()]);
        setGames(sortedGames);
      }
    } catch (error) {
      console.error('Error fetching user games:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine game type
  const getGameType = (game) => {
    // If game has settings with image, use the utility function
    if (
      game.game_settings &&
      game.game_settings.otherSettings &&
      game.game_settings.otherSettings.image
    ) {
      return getGameTypeUtil(game.game_settings.otherSettings.image);
    }

    // Fallback to name-based detection
    const name = game.name || '';
    if (name.includes('3D') || name.includes('WIN')) return '3D';
    if (name.includes('EZ2')) return '2D';
    if (name.includes('STL')) return 'STL';
    if (name.includes('L2')) return 'L2';
    if (name.includes('L3')) return 'L3';
    if (name.includes('S2')) return 'S2';
    if (name.includes('P3')) return 'P3';
    return 'Other';
  };

  // Filter games based on active tab
  const filteredGames =
    activeTab === 'All'
      ? games
      : games.filter((game) => getGameType(game) === activeTab);

  if (!isOpen) return null;

  // Component to display a single game
  const GameItem = ({ game }) => {
    // Format game reference and details
    const gameType = getGameType(game);
    let displayName = game.name;
    let referenceCode = '';
    // Get image based on game settings
    const GameIcon = game.game_settings?.otherSettings?.image
      ? getGameImage(getGameType(game))
      : null;

    // Special handling for specific game formats
    if (game.name.includes('WIN-')) {
      displayName = `(${gameType}) ${game.name}`;
      referenceCode = `(${gameType}) WIN-600`;
    } else if (game.name.includes('EZ2')) {
      displayName = 'EZ2 - Win550';
      referenceCode = 'EZ2';
    }

    // Format rates
    const rateFormat = (value) => {
      if (!value && value !== 0) return 'N/A';
      return typeof value === 'number' ? value.toFixed(2) : value;
    };

    const gameRates = {
      amount: rateFormat(game.winRate),
      percentage: rateFormat(game.commissionRate),
    };

    return (
      <div className='border border-gray-200 rounded-lg p-3 mb-4 hover:shadow-md transition-shadow'>
        <div className='flex items-start'>
          <div
            className={`w-20 h-20 text-white flex flex-col items-center justify-center rounded-lg mr-4`}
          >
            {GameIcon ? (
              <img src={GameIcon} className='w-15 h-15' />
            ) : (
              <>
                <span className='text-2xl font-bold'>{gameType}</span>
                <span className='text-xs font-light mt-1'>LOTTO</span>
              </>
            )}
          </div>
          <div className='flex-1'>
            <h3 className='font-semibold text-[16px] pb-2'>{displayName}</h3>
            <p className='text-[12px] text-gray-600 pb-2'>
              Game Reference:{' '}
              {game.game_settings?.otherSettings?.image ||
                referenceCode ||
                gameType}
            </p>
            <p className='text-[12px] text-gray-600'>
              Game Rates:{' '}
              <span className='font-semibold'>₱{gameRates.amount}</span> |{' '}
              <span className='font-semibold'>{gameRates.percentage}%</span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  const displayGames = filteredGames;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 z-[2000] flex justify-center items-center overflow-hidden pt-0'>
      <div
        className='bg-white rounded-lg w-full max-w-md mx-4'
        style={{
          maxHeight: '90vh',
          overflow: 'auto',
          marginTop: 'env(safe-area-inset-top)',
          marginBottom: 'env(safe-area-inset-bottom)',
          WebkitOverflowScrolling: 'touch', // For better iOS scrolling
        }}
      >
        {/* Blue Header - Make sticky */}
        <div className='bg-[#0E64D1] text-white p-4 rounded-t-lg flex justify-between items-center sticky top-0 z-10'>
          <h2 className='text-lg font-semibold'>
            {userData?.name} | Active Games Package
          </h2>
          <button onClick={onClose} className='text-white hover:text-gray-200'>
            <X size={24} />
          </button>
        </div>

        {/* Tabs - Make sticky */}
        <div className='sticky top-[60px] bg-white z-[5] p-3 border-b'>
          <div className='flex overflow-x-auto space-x-1 pb-2'>
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap ${
                  activeTab === tab
                    ? 'bg-[#65758B] text-white'
                    : 'text-[#65758B] hover:bg-gray-200 bg-[#E1E7EF]'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Games List */}
        <div className='p-4'>
          {loading ? (
            <div className='flex justify-center p-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
            </div>
          ) : displayGames.length > 0 ? (
            displayGames.map((game) => <GameItem key={game.id} game={game} />)
          ) : (
            <div className='text-center py-10 px-4'>
              <div className='w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='32'
                  height='32'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='text-gray-400'
                >
                  <circle cx='12' cy='12' r='10'></circle>
                  <line x1='4.93' y1='4.93' x2='19.07' y2='19.07'></line>
                </svg>
              </div>
              <h3 className='text-lg font-medium text-gray-700 mb-2'>
                No Games Available
              </h3>
              <p className='text-sm text-gray-500 max-w-xs mx-auto'>
                {activeTab !== 'All'
                  ? `No ${activeTab} games were found for this user. Try selecting a different game type.`
                  : "This user doesn't have any active games assigned yet."}
              </p>
              {activeTab !== 'All' && (
                <button
                  onClick={() => setActiveTab('All')}
                  className='mt-4 px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50'
                >
                  View All Games
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
